/**
 * CollectionSchemaEditor - Create/Edit collection schemas
 *
 * Provides a form-based interface for defining:
 * - Collection name and description
 * - Field definitions (name, type, options)
 * - Display configuration
 */

import { useState, useCallback } from 'react';
import type { CollectionSchema, FieldDefinition, FieldType } from '@deep-agents/shared-types';

// ============================================================
// ICONS
// ============================================================

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const GripIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

// ============================================================
// CONSTANTS
// ============================================================

const FIELD_TYPES: { value: FieldType; label: string; description: string }[] = [
  { value: 'string', label: 'Text', description: 'Single or multi-line text' },
  { value: 'number', label: 'Number', description: 'Numeric values' },
  { value: 'boolean', label: 'Yes/No', description: 'True or false toggle' },
  { value: 'enum', label: 'Select', description: 'Choose from predefined options' },
  { value: 'array', label: 'List', description: 'Multiple values (tags, etc.)' },
  { value: 'image', label: 'Image', description: 'Image URL or upload' },
  { value: 'video', label: 'Video', description: 'Video URL or upload' },
  { value: 'url', label: 'URL', description: 'Web link' },
  { value: 'date', label: 'Date', description: 'Date value' },
  { value: 'reference', label: 'Reference', description: 'Link to another collection' },
  { value: 'references', label: 'References', description: 'Links to multiple items' },
];

// ============================================================
// TYPES
// ============================================================

interface CollectionSchemaEditorProps {
  schema: CollectionSchema | null;
  isNew: boolean;
  onSave: (name: string, schema: CollectionSchema) => Promise<void>;
  onCancel: () => void;
  /** AI schema generation function (optional - only for new collections) */
  onGenerateSchema?: (description: string, examples?: string) => Promise<{ schema: CollectionSchema } | null>;
  /** Whether AI generation is in progress */
  isGeneratingSchema?: boolean;
}

// ============================================================
// COMPONENT
// ============================================================

export function CollectionSchemaEditor({
  schema,
  isNew,
  onSave,
  onCancel,
  onGenerateSchema,
  isGeneratingSchema = false,
}: CollectionSchemaEditorProps) {
  // Form state
  const [name, setName] = useState(schema?.name || '');
  const [label, setLabel] = useState(schema?.label || '');
  const [description, setDescription] = useState(schema?.description || '');
  const [fields, setFields] = useState<FieldDefinition[]>(schema?.fields || []);
  const [titleField, setTitleField] = useState(schema?.displayConfig?.titleField || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI Assistant modal state
  const [showAIModal, setShowAIModal] = useState(false);

  // ─────────────────────────────────────────────────────────
  // AI Schema Generation Handler
  // ─────────────────────────────────────────────────────────

  const handleApplyGeneratedSchema = useCallback((generatedSchema: CollectionSchema) => {
    // Apply the AI-generated schema to the form
    setName(generatedSchema.name);
    setLabel(generatedSchema.label);
    setDescription(generatedSchema.description || '');
    setFields(generatedSchema.fields);
    if (generatedSchema.displayConfig?.titleField) {
      setTitleField(generatedSchema.displayConfig.titleField);
    }
    setShowAIModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────
  // Field Management
  // ─────────────────────────────────────────────────────────

  const addField = useCallback(() => {
    const newField: FieldDefinition = {
      name: '',
      label: '',
      type: 'string',
      required: false,
      showInList: false,
      showInDetail: true,
    };
    setFields([...fields, newField]);
  }, [fields]);

  const updateField = useCallback((index: number, updates: Partial<FieldDefinition>) => {
    setFields(fields.map((field, i) =>
      i === index ? { ...field, ...updates } : field
    ));
  }, [fields]);

  const removeField = useCallback((index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  }, [fields]);

  const moveField = useCallback((index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  }, [fields]);

  // ─────────────────────────────────────────────────────────
  // Save Handler
  // ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError('Collection name is required');
      return;
    }

    if (!/^[a-z][a-z0-9_]*$/.test(name)) {
      setError('Name must start with a letter and contain only lowercase letters, numbers, and underscores');
      return;
    }

    if (!label.trim()) {
      setError('Display label is required');
      return;
    }

    if (fields.length === 0) {
      setError('At least one field is required');
      return;
    }

    // Validate fields
    for (const field of fields) {
      if (!field.name.trim()) {
        setError('All fields must have a name');
        return;
      }
      if (!field.label.trim()) {
        setError('All fields must have a label');
        return;
      }
      if (field.type === 'enum' && (!field.enumValues || field.enumValues.length === 0)) {
        setError(`Field "${field.label}" needs at least one option`);
        return;
      }
    }

    setError(null);
    setIsSaving(true);

    try {
      const schemaData: CollectionSchema = {
        name,
        label,
        description: description || undefined,
        fields,
        displayConfig: titleField ? { titleField } : undefined,
      };

      await onSave(name, schemaData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save collection');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon />
        </button>
        <h3 className="flex-1 text-lg font-semibold text-gray-900">
          {isNew ? 'New Collection' : `Edit: ${label || name}`}
        </h3>

        {/* AI Assistant Button - only show for new collections */}
        {isNew && onGenerateSchema && (
          <button
            onClick={() => setShowAIModal(true)}
            disabled={isGeneratingSchema}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <SparklesIcon />
            {isGeneratingSchema ? 'Generating...' : 'Generate with AI'}
          </button>
        )}
      </div>

      {/* AI Assistant Modal */}
      {showAIModal && onGenerateSchema && (
        <AISchemaModal
          isGenerating={isGeneratingSchema}
          onGenerate={onGenerateSchema}
          onApply={handleApplyGeneratedSchema}
          onClose={() => setShowAIModal(false)}
        />
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700">Basic Information</h4>

        <div className="grid grid-cols-2 gap-4">
          {/* Name (ID) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Name (ID) *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="exercises"
              disabled={!isNew}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Lowercase, no spaces (used in code)
            </p>
          </div>

          {/* Label */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Display Label *
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Exercises"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this collection contain?"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Fields</h4>
          <button
            onClick={addField}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-600 hover:bg-violet-50 rounded transition-colors"
          >
            <PlusIcon />
            Add Field
          </button>
        </div>

        {fields.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <p className="text-sm text-gray-500">No fields defined yet</p>
            <button
              onClick={addField}
              className="mt-2 text-sm text-violet-600 hover:text-violet-700"
            >
              Add your first field
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {fields.map((field, index) => (
              <FieldEditor
                key={index}
                field={field}
                index={index}
                totalFields={fields.length}
                allCollections={[]} // TODO: Pass available collections for references
                onChange={(updates) => updateField(index, updates)}
                onRemove={() => removeField(index)}
                onMove={(direction) => moveField(index, direction)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Display Config */}
      {fields.length > 0 && (
        <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700">Display Settings</h4>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Title Field
            </label>
            <select
              value={titleField}
              onChange={(e) => setTitleField(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">-- Select field --</option>
              {fields.filter(f => f.type === 'string').map(f => (
                <option key={f.name} value={f.name}>{f.label || f.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Which field to show as the item title in lists
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : (isNew ? 'Create Collection' : 'Save Changes')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// FIELD EDITOR SUB-COMPONENT
// ============================================================

interface FieldEditorProps {
  field: FieldDefinition;
  index: number;
  totalFields: number;
  allCollections: string[];
  onChange: (updates: Partial<FieldDefinition>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}

function FieldEditor({
  field,
  index,
  totalFields,
  allCollections,
  onChange,
  onRemove,
  onMove,
}: FieldEditorProps) {
  const [isExpanded, setIsExpanded] = useState(!field.name); // Expand new fields

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Field Header (always visible) */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50">
        {/* Drag Handle */}
        <div className="text-gray-400 cursor-move">
          <GripIcon />
        </div>

        {/* Field Name & Type */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 text-left"
        >
          <span className="text-sm font-medium text-gray-900">
            {field.label || field.name || 'New Field'}
          </span>
          <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-200 rounded">
            {FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}
          </span>
          {field.required && (
            <span className="text-xs text-red-500">*</span>
          )}
        </button>

        {/* Move Buttons */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={index === totalFields - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Delete */}
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
        >
          <TrashIcon />
        </button>
      </div>

      {/* Expanded Editor */}
      {isExpanded && (
        <div className="p-3 space-y-3 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-3">
            {/* Field Name (ID) */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Field Name *
              </label>
              <input
                type="text"
                value={field.name}
                onChange={(e) => onChange({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                placeholder="field_name"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            {/* Label */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Label *
              </label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder="Field Label"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Type *
              </label>
              <select
                value={field.type}
                onChange={(e) => onChange({ type: e.target.value as FieldType })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {FIELD_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Type-specific options */}
          {field.type === 'enum' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Options (comma-separated) *
              </label>
              <input
                type="text"
                value={field.enumValues?.join(', ') || ''}
                onChange={(e) => onChange({
                  enumValues: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                })}
                placeholder="option1, option2, option3"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          )}

          {field.type === 'array' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Array Item Type
              </label>
              <select
                value={field.arrayType || 'string'}
                onChange={(e) => onChange({ arrayType: e.target.value as 'string' | 'number' })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="string">Text</option>
                <option value="number">Number</option>
              </select>
            </div>
          )}

          {(field.type === 'reference' || field.type === 'references') && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Target Collection
              </label>
              <input
                type="text"
                value={field.refCollection || ''}
                onChange={(e) => onChange({ refCollection: e.target.value })}
                placeholder="collection_name"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Description
            </label>
            <input
              type="text"
              value={field.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="What is this field for?"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={field.required || false}
                onChange={(e) => onChange({ required: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              Required
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={field.showInList || false}
                onChange={(e) => onChange({ showInList: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              Show in list
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={field.searchable || false}
                onChange={(e) => onChange({ searchable: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              Searchable
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={field.filterable || false}
                onChange={(e) => onChange({ filterable: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              Filterable
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AI SCHEMA MODAL SUB-COMPONENT
// ============================================================

interface AISchemaModalProps {
  isGenerating: boolean;
  onGenerate: (description: string, examples?: string) => Promise<{ schema: CollectionSchema } | null>;
  onApply: (schema: CollectionSchema) => void;
  onClose: () => void;
}

function AISchemaModal({
  isGenerating,
  onGenerate,
  onApply,
  onClose,
}: AISchemaModalProps) {
  const [description, setDescription] = useState('');
  const [examples, setExamples] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generatedSchema, setGeneratedSchema] = useState<CollectionSchema | null>(null);

  // Example descriptions to help users get started
  const suggestions = [
    'A recipe collection with ingredients, instructions, prep time, and difficulty level',
    'A product catalog with name, price, category, and images',
    'A workout exercise library with muscle groups, equipment, and difficulty',
    'A movie collection with title, genre, rating, and release year',
  ];

  const handleGenerate = async () => {
    if (!description.trim()) {
      setError('Please describe what you want to store');
      return;
    }

    setError(null);
    const result = await onGenerate(description, examples || undefined);

    if (result) {
      setGeneratedSchema(result.schema);
    } else {
      setError('Failed to generate schema. Please try again with a different description.');
    }
  };

  const handleApply = () => {
    if (generatedSchema) {
      onApply(generatedSchema);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-2">
            <SparklesIcon />
            <h3 className="text-lg font-semibold text-gray-900">AI Schema Assistant</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!generatedSchema ? (
            <>
              {/* Description Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What do you want to store?
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your collection in plain language..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>

              {/* Suggestions */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Try one of these:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setDescription(suggestion)}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-600 hover:bg-violet-100 hover:text-violet-700 rounded transition-colors"
                    >
                      {suggestion.slice(0, 40)}...
                    </button>
                  ))}
                </div>
              </div>

              {/* Examples (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Examples (optional)
                </label>
                <textarea
                  value={examples}
                  onChange={(e) => setExamples(e.target.value)}
                  placeholder="Give a few example items to help the AI understand better..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  E.g., "Chocolate Cake - 45 min, easy" or "Nike Air Max - $120, Running"
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Generated Schema Preview */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">
                  Schema generated successfully!
                </p>
                <p className="text-sm text-green-700">
                  <strong>{generatedSchema.label}</strong> with {generatedSchema.fields.length} fields
                </p>
              </div>

              {/* Schema Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Name</p>
                  <p className="text-sm text-gray-900">{generatedSchema.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Description</p>
                  <p className="text-sm text-gray-700">{generatedSchema.description || 'No description'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Fields</p>
                  <div className="space-y-1">
                    {generatedSchema.fields.map((field, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-900 font-medium">{field.label}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">
                          {field.type}
                        </span>
                        {field.required && (
                          <span className="text-xs text-red-500">*</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                You can modify the schema after applying it.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          {!generatedSchema ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !description.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
              >
                <SparklesIcon />
                {isGenerating ? 'Generating...' : 'Generate Schema'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setGeneratedSchema(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
              >
                Apply Schema
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
